// The staff app's school calendar (PMU-265): a teacher's own timetable (what they teach, in every
// class, and their own class's lessons) a day at a time, the school calendar (staff items too), and
// a card on Now and Class with the lesson on now and the next one. School staff with calendar:view;
// bus crews do not have it.
import { useEffect, useState } from "react";
import { Icon, Spinner } from "@vavr/ui";
import { api, navigate, usePath } from "@vavr/screens/shared";
import { T, useTeacher } from "@vavr/screens/teacher";
import { clock, EventList, longDay, WEEKDAYS, type CalEvent, type Period, type Slot } from "./screens.tsx";

interface Cal { today: string; events: CalEvent[]; schedules: { id: string; name: string; is_default: number; periods: Period[] }[] }
interface Tts { rooms: { id: string; name: string; teacher_ids: string[] }[]; timetables: { room_id: string; slots: Slot[] }[] }
interface Lesson { weekday: number; period: Period; subject: string; room: string; location: string | null; mine: boolean }

/** Both reads, kept for the app session per site (the card and the page share them). */
const cache = new Map<string, { cal: Cal; tts: Tts }>();
function useStaffCalendar(siteId: string, version: number) {
  const [d, setD] = useState(cache.get(siteId) ?? null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    Promise.all([api<Cal>("GET", `/api/v1/sites/${siteId}/calendar`), api<Tts>("GET", `/api/v1/sites/${siteId}/timetables`)])
      .then(([cal, tts]) => { if (live) { cache.set(siteId, { cal, tts }); setD({ cal, tts }); } }, (e: Error) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [siteId, version]);
  return { d, error };
}

/** What this person teaches: slots naming them, and their own classes' slots that name nobody. */
function lessonsOf(meId: string, d: { cal: Cal; tts: Tts }): Lesson[] {
  const usual = d.cal.schedules.find((s) => s.is_default) ?? d.cal.schedules[0];
  if (!usual) return [];
  const mineRooms = new Set(d.tts.rooms.filter((r) => r.teacher_ids.includes(meId)).map((r) => r.id));
  const out: Lesson[] = [];
  for (const t of d.tts.timetables) for (const s of t.slots) {
    const named = s.teacher_account_id === meId;
    if (!named && !(mineRooms.has(t.room_id) && !s.teacher_account_id)) continue;
    const period = usual.periods.find((p) => p.code === s.period_code);
    if (period) out.push({ weekday: s.weekday, period, subject: s.subject, room: d.tts.rooms.find((r) => r.id === t.room_id)?.name ?? "Class", location: s.location, mine: named });
  }
  return out.sort((a, b) => a.period.start_time.localeCompare(b.period.start_time));
}

const weekdayOf = (d: string) => new Date(`${d}T12:00:00Z`).getUTCDay();
const nowHHMM = (tz: string) => new Intl.DateTimeFormat("en-GB", { timeZone: tz, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date());
/** Today, if the school closed or moved it. */
const dayNote = (events: CalEvent[], date: string) => events.find((e) => e.start_date <= date && e.end_date >= date && (e.kind === "no_school" || e.kind === "holiday" || e.kind === "early_release" || e.kind === "late_start"));

/** Under Now and Class: the lesson on now and the next one, or why there are none today. */
export function StaffCalendarCard() {
  const { site, me, version } = useTeacher();
  const { d } = useStaffCalendar(site.id, version);
  // Not on the page it opens.
  const here = usePath().startsWith(`${T}/calendar`);
  if (!d || here) return null;
  const today = d.cal.today, wd = weekdayOf(today), now = nowHHMM(site.tz);
  const lessons = lessonsOf(me.id, d).filter((l) => l.weekday === wd);
  const note = dayNote(d.cal.events, today);
  const current = lessons.find((l) => l.period.start_time <= now && l.period.end_time > now);
  const next = lessons.find((l) => l.period.start_time > now);
  const line = note?.kind === "no_school" || note?.kind === "holiday" ? `Today: ${note.title}`
    : current ? `Now: ${current.subject} · ${current.room}${next ? ` · next ${clock(next.period.start_time)} ${next.subject}` : ""}`
    : next ? `Next: ${clock(next.period.start_time)} ${next.subject} · ${next.room}`
    : lessons.length ? "No more lessons today" : "Your timetable and the school calendar";
  return (
    <button type="button" className="plf-card pcal-card" onClick={() => navigate(`${T}/calendar`)}>
      <span className="plf-card__icon" aria-hidden="true"><Icon name="calendar" /></span>
      <span className="plf-card__main"><b>My timetable</b><span className="pmu-small pmu-muted">{line}{note?.kind === "early_release" || note?.kind === "late_start" ? ` · ${note.title}` : ""}</span></span>
      <Icon name="chevron-right" />
    </button>
  );
}

/** The page: my timetable a day at a time, then the school calendar. */
export function StaffCalendarPage() {
  const { site, me, version } = useTeacher();
  const { d, error } = useStaffCalendar(site.id, version);
  const [day, setDay] = useState<number | null>(null);
  if (error) return <p className="pmu-muted">{error}</p>;
  if (!d) return <div className="pmu-center"><Spinner label="Loading" /></div>;
  const today = d.cal.today, t = weekdayOf(today);
  const shown = day ?? (t === 0 || t === 6 ? 1 : t);
  const lessons = lessonsOf(me.id, d);
  const usual = d.cal.schedules.find((s) => s.is_default) ?? d.cal.schedules[0];
  const list = (usual?.periods ?? []).map((p) => ({ p, l: lessons.find((x) => x.weekday === shown && x.period.code === p.code) })).filter((x) => x.l || x.p.kind !== "class");
  const note = dayNote(d.cal.events, today);
  return (
    <div className="pmu-stack pcal-parent">
      <article className="pcal-day-tt">
        <header><b>My timetable</b><span className="pmu-small pmu-muted">{usual ? usual.name : "No bell schedule yet"}</span></header>
        {shown === t && note && <p className="pmu-small"><b>Today: {note.title}</b></p>}
        <div className="pcal-switch" role="tablist" aria-label="Day">
          {WEEKDAYS.filter((w) => w.d >= 1 && w.d <= 5).map((w) => <button type="button" role="tab" key={w.d} aria-selected={w.d === shown} onClick={() => setDay(w.d)}>{w.l}{w.d === t ? " ·" : ""}</button>)}
        </div>
        {!usual ? <p className="pmu-small pmu-muted">The office has not set the bell schedule yet.</p>
          : !list.some((x) => x.l) ? <p className="pmu-small pmu-muted">No lessons for you on {WEEKDAYS.find((w) => w.d === shown)?.long}.</p> : (
          <ol className="pcal-day-tt__list">
            {list.map(({ p, l }) => (
              <li key={p.code} className={!l ? "pcal-day-tt__break" : undefined}>
                <span className="pcal-day-tt__time">{clock(p.start_time)}</span>
                <span><b>{l?.subject ?? p.name}</b>{l && <span className="pmu-small pmu-muted"> {[l.room, l.location].filter(Boolean).join(" · ")}</span>}</span>
              </li>
            ))}
          </ol>
        )}
        {shown === t && <p className="pmu-small pmu-muted">Today, {longDay(today)}</p>}
      </article>
      <h3 className="pcal-parent__h">School calendar</h3>
      <EventList events={d.cal.events.filter((e) => e.end_date >= today)} empty="Nothing on the calendar yet." level={4} />
    </div>
  );
}
